import React from "react";
import { useTranslation } from "react-i18next";
import {
  status,
  task as mockTask,
  Identifiable,
  Project,
  allocation,
  id,
} from "@planzone/dto";
import {
  useAuthentication,
  useListsRetrieval,
  useProjectResourcesRetrieval,
  useTaskStatusesRetrieval,
  useTaskCreation,
} from "@planzone/view-models";
import { events } from "@planzone/events";
import { WorkspaceContext } from "@scenes/workspaceContext";
import { useMessage } from "@components/useMessage";
import { Button } from "@common/button";
import { Modal } from "@common/modal";
import { TaskCreationForm } from "./taskCreationForm";
import { Props } from "./types";
import styles from "./styles.scss";

function TaskCreation({
  project,
  list,
  task,
  isMultiProject,
  initialDate,
  cancel,
}: Props) {
  const filter = { limit: 999999999999, start: 0 };
  const { t } = useTranslation();
  const { isRequestPending, error, createTask } = useTaskCreation();
  const { account } = useAuthentication();

  const [formData, editTaskCreationForm] = React.useState({
    ...mockTask,
    activityKeyRank: -1,
    ...(initialDate && { startDate: initialDate, endDate: initialDate }),
    allocations:
      mockTask?.allocations && mockTask.allocations.length > 0
        ? mockTask.allocations
        : account && account.resource
          ? [
              {
                ...allocation,
                uuid: id,
                resource: account.resource,
              },
            ]
          : [],
  });

  const [selectedProject, setSelectedProject] =
    React.useState<Identifiable<Project> | null>(null);

  const currentProject = selectedProject || project;

  const {
    isRequestPending: isTaskStatusesRetrievalRequestPending,
    isRequestSuccess: isTaskStatusesRetrievalRequestSuccess,
    isRequestFailure: isTaskStatusesRetrievalRequestFailure,
    statuses,
    retrieveTaskStatuses,
  } = useTaskStatusesRetrieval(currentProject);

  const {
    isRequestPending: isResourcesRequestPending,
    isRequestSuccess: isResourcesRequestSuccess,
    isRequestFailure: isResourcesRequestFailure,
    retrieveProjectResources,
  } = useProjectResourcesRetrieval(currentProject);

  React.useEffect(() => {
    !!currentProject.uuid &&
      !isResourcesRequestPending &&
      !isResourcesRequestSuccess &&
      !isResourcesRequestFailure &&
      retrieveProjectResources(currentProject);
  }, [currentProject.uuid]);

  const todoStatus = statuses.find(
    ({ percentComplete, basic }) => basic && percentComplete === 0,
  );
  const [firstStatus] = statuses;
  const taskStatus = todoStatus || firstStatus || status;
  const hasStatus = statuses.find(({ uuid }) => uuid === formData.status.uuid);
  const {
    lists,
    isRequestPending: isRetrieveListsRequestPending,
    isRequestSuccess: isRetrieveListsRequestSuccess,
    isRequestFailure: isRetrieveListsRequestFailure,
    retrieveLists,
  } = useListsRetrieval(currentProject, filter);
  const [selectedList, setSelectedList] = React.useState(list);

  React.useEffect(() => {
    !!currentProject.uuid &&
      !isRetrieveListsRequestPending &&
      !isRetrieveListsRequestSuccess &&
      !isRetrieveListsRequestFailure &&
      retrieveLists({ project: currentProject, filter });
  }, [currentProject.uuid]);

  React.useEffect(() => {
    !!currentProject.uuid && retrieveProjectResources(currentProject);
  }, [currentProject.uuid]);

  React.useEffect(() => {
    !isTaskStatusesRetrievalRequestPending &&
      !isTaskStatusesRetrievalRequestSuccess &&
      !isTaskStatusesRetrievalRequestFailure &&
      retrieveTaskStatuses(currentProject);
  }, [
    currentProject,
    isTaskStatusesRetrievalRequestPending,
    isTaskStatusesRetrievalRequestSuccess,
    isTaskStatusesRetrievalRequestFailure,
    retrieveTaskStatuses,
  ]);

  React.useEffect(() => {
    if (!task) return;
    const base = statuses.length > 0 ? { ...task, status: taskStatus } : task;
    editTaskCreationForm({
      ...base,
      allocations: task.allocations.map(({ uuid: _uuid, ...alloc }) => ({
        ...alloc,
        uuid: id,
        completedAt: "",
      })),
    });
  }, [statuses]);

  useMessage(
    ({ type }) =>
      type === events.createTaskSucceeded && editTaskCreationForm(mockTask),
    { localOnly: true },
  );

  return (
    <WorkspaceContext type="project" project={currentProject}>
      <Modal
        header={
          task
            ? t("taskCreation.duplicateTitle") + "« " + task.name + " »"
            : t("taskCreation.title")
        }
        close={cancel}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const resolvedStatus = hasStatus ? formData.status : taskStatus;
            !isRequestPending &&
              createTask({
                list: selectedList,
                task: {
                  ...formData,
                  status: resolvedStatus,
                  allocations: formData.allocations.map((alloc) => ({
                    ...alloc,
                    percentComplete: resolvedStatus.percentComplete ?? 0,
                  })),
                },
                project: currentProject,
              });
          }}
          className={styles.taskCreation}
        >
          <div className={styles.main}>
            <div className={styles.form}>
              <div className={styles.form}>
                <TaskCreationForm
                  formData={{
                    ...formData,
                    status: hasStatus ? formData.status : taskStatus,
                  }}
                  error={error}
                  project={currentProject}
                  list={selectedList}
                  lists={
                    isMultiProject && selectedProject?.rootList
                      ? [selectedProject.rootList]
                      : [lists]
                  }
                  editForm={editTaskCreationForm}
                  setList={setSelectedList}
                  isMultiProject={isMultiProject}
                  onProjectChange={(project) => {
                    setSelectedProject(project);
                    if (project?.rootList) {
                      setSelectedList(project.rootList);
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <div className={styles.buttons}>
            <Button type="cancel" press={cancel}>
              {t("taskCreation.cancel")}
            </Button>
            <Button
              type="submit"
              status={isRequestPending ? "pending" : "none"}
              isDisabled={isMultiProject && !selectedProject}
            >
              {task ? t("taskCreation.duplicate") : t("taskCreation.submit")}
            </Button>
          </div>
        </form>
      </Modal>
    </WorkspaceContext>
  );
}

export { TaskCreation };
